"use client";

import { useState } from "react";
import { Button } from "@/components/ui/button";

/* Brand marks. Kept as inline SVG so they render without an icon dependency. */

function GoogleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1Z"
        fill="#4285F4"
      />
      <path
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23Z"
        fill="#34A853"
      />
      <path
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18A10.96 10.96 0 0 0 1 12c0 1.77.43 3.45 1.18 4.93l3.66-2.84Z"
        fill="#FBBC05"
      />
      <path
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53Z"
        fill="#EA4335"
      />
    </svg>
  );
}

function FacebookIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="#1877F2"
        d="M24 12.07C24 5.4 18.63 0 12 0S0 5.4 0 12.07C0 18.1 4.39 23.1 10.13 24v-8.44H7.08v-3.49h3.05V9.41c0-3.02 1.79-4.69 4.53-4.69 1.31 0 2.68.24 2.68.24v2.97h-1.51c-1.49 0-1.96.93-1.96 1.89v2.25h3.33l-.53 3.49h-2.8V24C19.61 23.1 24 18.1 24 12.07Z"
      />
    </svg>
  );
}

function MicrosoftIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#F25022" d="M1 1h10v10H1z" />
      <path fill="#7FBA00" d="M13 1h10v10H13z" />
      <path fill="#00A4EF" d="M1 13h10v10H1z" />
      <path fill="#FFB900" d="M13 13h10v10H13z" />
    </svg>
  );
}

const PROVIDERS = [
  { name: "Google", Icon: GoogleIcon },
  { name: "Facebook", Icon: FacebookIcon },
  { name: "Microsoft", Icon: MicrosoftIcon },
] as const;

/**
 * Social sign-in options. The buttons are present and styled; the providers
 * are not wired up yet, so clicking one explains that and points the visitor
 * back to email sign-in.
 */
export function SocialAuthButtons({ action }: { action: "Sign in" | "Sign up" }) {
  const [showNote, setShowNote] = useState(false);

  return (
    <div className="flex flex-col gap-3">
      {/* Divider */}
      <div className="relative flex items-center gap-4">
        <span className="h-px flex-1 bg-line" aria-hidden="true" />
        <span className="text-xs uppercase tracking-[0.12em] text-mid">
          or continue with
        </span>
        <span className="h-px flex-1 bg-line" aria-hidden="true" />
      </div>

      {PROVIDERS.map(({ name, Icon }) => (
        <Button
          key={name}
          variant="outline"
          type="button"
          className="w-full"
          aria-label={`${action} with ${name}`}
          onClick={() => setShowNote(true)}
        >
          <Icon className="size-5" />
          {name}
        </Button>
      ))}

      {showNote && (
        <p role="status" className="text-center text-xs text-mid">
          Social sign-in is coming soon. {action} with your email for now.
        </p>
      )}
    </div>
  );
}
